import React, { Suspense } from "react"; // Moved up
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from "@/hooks/useAuth";
import ScrollToTop from "@/components/ScrollToTop";
import ProtectedRoute from "@/components/ProtectedRoute";
import DevToolsDetector from "@/components/DevToolsDetector";

// Lazy Loaded Components
const WhatsAppButton = React.lazy(() => import("@/components/WhatsAppButton"));
const MobileFooter = React.lazy(() => import("@/mobile/MobileFooter").then(module => ({ default: module.MobileFooter })));

// Components
import Loader from "@/components/Loader";

// Pages
import Index from "./pages/Index"; // Keep Index eager for LCP
// Lazy Load AuthPage to reduce initial bundle size
const AuthPage = React.lazy(() => import("./pages/AuthPage").then(module => ({ default: module.AuthPage })));
const UpdatePassword = React.lazy(() => import("./pages/UpdatePassword").then(module => ({ default: module.UpdatePassword })));

// Lazy Load requested pages
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const AdminDashboard = React.lazy(() => import("./pages/AdminDashboard"));
const TaxReturnsDashboard = React.lazy(() => import("./pages/TaxReturnsDashboard")); // Admin sub-page
const About = React.lazy(() => import("./pages/About"));
const Services = React.lazy(() => import("./pages/Services"));
const Contact = React.lazy(() => import("./pages/Contact"));
const TaxCalculator = React.lazy(() => import("./pages/TaxCalculator"));
const NTNVerification = React.lazy(() => import("./pages/NTNVerification"));
const ATLVerification = React.lazy(() => import("./pages/ATLVerification"));
const Terms = React.lazy(() => import("./pages/Terms"));
const Privacy = React.lazy(() => import("./pages/Privacy"));
const FAQs = React.lazy(() => import("./pages/FAQs"));
const Shop = React.lazy(() => import("./pages/Shop"));
import TaxReturnLanding from "./pages/TaxReturnLanding";
import TaxReturn from "./pages/TaxReturn";
import { SuccessFiling } from "./pages/SuccessFiling";
const Checkout = React.lazy(() => import("./pages/Checkout"));
const OrderSuccess = React.lazy(() => import("./pages/OrderSuccess"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const TrackOrder = React.lazy(() => import("./pages/TrackOrder"));
const AiAssistant = React.lazy(() => import("./pages/AiAssistant"));
const Reviews = React.lazy(() => import("./pages/Reviews"));
const AdminRestore = React.lazy(() => import("./pages/AdminRestore"));
const Cookies = React.lazy(() => import("./pages/Cookies"));
const Pricing = React.lazy(() => import("./pages/Pricing"));
const Status = React.lazy(() => import("./pages/Status"));

// --- ADDED FOR BLOG (Lazy Loaded) ---
const Blog = React.lazy(() => import("./pages/Blog"));
const BlogPost = React.lazy(() => import("./pages/BlogPost"));
// --- END OF ADDITION ---

const queryClient = new QueryClient();

import { CartProvider } from "@/context/CartContext";
import { MaintenanceWrapper } from "@/components/MaintenanceWrapper";
import { SecurityGuard } from "@/components/SecurityGuard";
const VisitorTracker = React.lazy(() => import("@/components/VisitorTracker").then(m => ({ default: m.VisitorTracker })));

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <SecurityGuard>
        <TooltipProvider>
      <Toaster />
      <Sonner position="top-center" richColors closeButton />
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <Suspense fallback={null}>
              <VisitorTracker />
            </Suspense>
            <ScrollToTop />
            <DevToolsDetector />
            <Suspense fallback={<Loader />}>
              <MaintenanceWrapper>
                <main className="pb-20 lg:pt-0 lg:pb-0">
                  <Routes>
                    {/* --- Public Routes --- */}
                    <Route path="/" element={<Index />} />
                    <Route path="/login" element={<AuthPage />} />
                    <Route path="/signup" element={<AuthPage />} />
                    <Route path="/update-password" element={<UpdatePassword />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/tax-calculator" element={<TaxCalculator />} />
                    <Route path="/ntn-verification" element={<NTNVerification />} />
                    <Route path="/atl-verification" element={<ATLVerification />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/faqs" element={<FAQs />} />
                    <Route path="/shop" element={<Shop />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/order-success" element={<OrderSuccess />} />
                    <Route path="/tax-return-info" element={<TaxReturnLanding />} />
                    <Route path="/track-order" element={<TrackOrder />} />
                    <Route path="/reviews" element={<Reviews />} />
                    <Route path="/cookies" element={<Cookies />} />
                    <Route path="/pricing" element={<Pricing />} />

                    {/* 3. ADD THE NEW AI PAGE ROUTE */}
                    <Route path="/ai" element={<AiAssistant />} />
                    <Route path="/status" element={<Status />} />

                    {/* --- ADDED FOR BLOG --- */}
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    {/* --- END OF ADDITION --- */}

                    {/* --- Protected Routes --- */}
                    <Route
                      path="/tax-return"
                      element={
                        <ProtectedRoute>
                          <TaxReturn />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute>
                          <AdminDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/success-filing"
                      element={
                        <ProtectedRoute>
                          <SuccessFiling />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/tax-returns"
                      element={
                        <ProtectedRoute>
                          <TaxReturnsDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/admin-restore" element={<AdminRestore />} />

                    {/* --- 404 Catch-all Route --- */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </main>
              </MaintenanceWrapper>
              <Suspense fallback={null}>
                <MobileFooter />
              </Suspense>
            </Suspense>
            <Suspense fallback={null}>
              <WhatsAppButton />
            </Suspense>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
    </SecurityGuard>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;
